import {
  isDefined,
  isValidId,
  presentText,
  toTitleCase,
} from "@timeedit/registration-shared";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useIsDesktop } from "@timeedit/registration-components";
import type { BreadcrumbItem } from "@timeedit/ui-components";
import { setBreadcrumbs } from "../redux/slices/navigation";
import { FrontendRegistration } from "@timeedit/registration-schemas";

/**
 * @description Detects the current route and updates breadcrumbs in the Redux store.
 * Handles these routes:
 * - /<customerSignature>/registration
 * - /<customerSignature>/registration/<activityId>
 * - /<customerSignature>/schedule
 *
 * For activityId routes, combines course and activity information to create the breadcrumb title (e.g., "Course - Activity")
 * @param courses - The courses related to the student.
 * @param activities - The activities related to the student.
 */
export function useDetectBreadcrumbs({
  activities,
  courses,
}: Pick<FrontendRegistration, "activities" | "courses">): void {
  const dispatch = useDispatch();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!dispatch) return;

    // Remove first empty element and customer signature
    const pathSegments = location.pathname.split("/").slice(2);

    const items = pathSegments.reduce(
      (
        items: { path: string; breadCrumbItems: BreadcrumbItem[] },
        currentSegment
      ) => {
        items.path = items.path
          ? `${items.path}/${currentSegment}`
          : currentSegment;

        // Check if this is an activityId (numeric)
        if (isValidId(Number(currentSegment))) {
          // Only add activity breadcrumb for mobile
          if (isDesktop) return items;

          const activity = activities[currentSegment];
          if (!isDefined(activity)) return items;

          const course = courses[activity.parentId];
          if (!isDefined(course)) return items;

          items.breadCrumbItems.push({
            title: `${presentText(course.label, 10)} - ${activity.label}`,
            href: items.path,
          });
        } else {
          items.breadCrumbItems.push({
            title: toTitleCase(currentSegment),
            href: items.path,
          });
        }

        return items;
      },
      { path: "", breadCrumbItems: [] }
    );

    dispatch(setBreadcrumbs(items.breadCrumbItems));
  }, [location.pathname, dispatch, activities, courses, isDesktop]);
}
